import { FC } from 'react';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';

import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import {
    formatCompactChartValue,
    getBottomLegend,
    getCartesianChartMargin,
    getChartFormatting,
    humanizeChartLabel,
} from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportData } from '../useNivoReportData';
import { useNivoReportFormatting } from '../useNivoReportFormatting';
import { fallbackScatterPlotData } from './fallbackScatterPlotData';

interface ScatterPlotProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

const ScatterPlot: FC<ScatterPlotProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();
    const chartData = useNivoReportData(component, fallbackScatterPlotData);
    const { financialValueDisplay, formatMetricValue, getMetricLabel } = useNivoReportFormatting(component);
    const config = component.configuration as ReportComponentConfig;
    const formatting = getChartFormatting(config);
    const margin = getCartesianChartMargin({
        showAxisLabels: formatting.showAxisLabels,
        showLegend: formatting.showLegend,
    });

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsiveScatterPlot
                data={chartData}
                margin={margin}
                xScale={{ type: 'linear', min: 0, max: 'auto', nice: true }}
                xFormat={(value) => formatMetricValue(value, 0)}
                yScale={{ type: 'linear', min: 0, max: 'auto', nice: true }}
                yFormat={(value) => formatMetricValue(value, 1)}
                motionConfig={'gentle'}
                colors={colorsArray}
                theme={nivoChartTheme}
                blendMode='normal'
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    // orient: 'bottom',
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    format: (value) =>
                        formatCompactChartValue(
                            value,
                            (nextValue) => formatMetricValue(nextValue, 0),
                            financialValueDisplay,
                        ),
                    legend: formatting.showAxisLabels ? getMetricLabel(0) : undefined,
                    legendPosition: 'middle',
                    legendOffset: 40,
                    truncateTickAt: 14,
                }}
                axisLeft={{
                    // orient: 'left',
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    format: (value) =>
                        formatCompactChartValue(
                            value,
                            (nextValue) => formatMetricValue(nextValue, 1),
                            financialValueDisplay,
                        ),
                    legend: formatting.showAxisLabels ? getMetricLabel(1) : undefined,
                    legendPosition: 'middle',
                    legendOffset: -56,
                    truncateTickAt: 14,
                }}
                tooltip={({ node }) => (
                    <CustomNivoChartTooltip
                        title={humanizeChartLabel(node.serieId)}
                        entries={[
                            {
                                color: node.color,
                                label: getMetricLabel(0),
                                value: node.xValue,
                            },
                            {
                                color: node.color,
                                label: getMetricLabel(1),
                                value: node.yValue,
                            },
                        ]}
                        formatValue={(value) => formatMetricValue(value)}
                    />
                )}
                legends={formatting.showLegend ? getBottomLegend({ translateY: 64 }) : []}
                // legends={[
                //     {
                //         anchor: 'bottom',
                //         direction: 'row',
                //         justify: false,
                //         translateX: 0,
                //         translateY: 96,
                //         itemsSpacing: 2,
                //         itemDirection: 'left-to-right',
                //         itemWidth: 80,
                //         itemHeight: 20,
                //         itemOpacity: 1,
                //         // itemBackground: token.colorBorder',
                //         symbolSize: 12,
                //         symbolShape: 'circle',
                //         toggleSerie: true,
                //         symbolBorderColor: 'rgba(0, 0, 0, .5)',
                //         effects: [
                //             {
                //                 on: 'hover',
                //                 style: {
                //                     itemBackground: 'rgba(0, 0, 0, .03)',
                //                     itemOpacity: 1,
                //                 },
                //             },
                //         ],
                //     },
                // ]}
            />
        </div>
    );
};

export default ScatterPlot;
